// Keeps a browser tab's sign-in alive across long gaps.
//
// The failure this exists to prevent: a learner leaves the app open, comes back
// hours or days later, and the tab fires its whole start-up burst of requests at
// once against a token that expired while it sat idle. Every one of them 401s
// together, and the odds that something in that pile ends the session are much
// higher than they should be.
//
// Instead, refresh once — quietly, before those requests go out — whenever the
// tab wakes up with a token that is near its end. The tab also watches for
// other tabs signing in or out so every open tab agrees on who is signed in.

import { refreshAccessToken } from './api';

// Refresh when the access token has less than this left. Comfortably longer
// than any realistic reading session, so the token in hand never expires
// mid-use.
const REFRESH_WHEN_REMAINING_MS = 2 * 24 * 60 * 60 * 1000;

// Decode a JWT payload without a library. Never throws: an unreadable token is
// treated as "no idea when this expires", which simply means no early refresh.
const readTokenPayload = (token) => {
  try {
    const payload = String(token).split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
};

export const tokenExpiresAt = (token) => {
  const payload = readTokenPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
};

const needsEarlyRefresh = (token) => {
  const expiresAt = tokenExpiresAt(token);
  if (expiresAt === null) return false;
  return expiresAt - Date.now() <= REFRESH_WHEN_REMAINING_MS;
};

export function installSessionPersistence() {
  let inFlight = false;

  const keepAlive = () => {
    if (inFlight) return;
    if (localStorage.getItem('guestMode') === 'true') return;
    const token = localStorage.getItem('token');
    if (!token || !localStorage.getItem('refreshToken')) return;
    if (!needsEarlyRefresh(token)) return;

    inFlight = true;
    // Failures are deliberately silent: the response interceptor already knows
    // how to tell a dead session from a bad moment, and a keep-alive that
    // cannot reach the network must never be what signs somebody out.
    refreshAccessToken(token)
      .catch(() => {})
      .finally(() => { inFlight = false; });
  };

  const onVisibility = () => {
    if (!document.hidden) keepAlive();
  };

  // Another tab signed in or out. Without this, a second open tab keeps
  // rendering a signed-in shell it no longer has an identity for.
  const onStorage = (event) => {
    if (event.key !== 'token') return;
    if (event.newValue) return;
    if (localStorage.getItem('guestMode') === 'true') return;
    window.dispatchEvent(new CustomEvent('sessionExpired'));
  };

  keepAlive();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', keepAlive);
  window.addEventListener('online', keepAlive);
  window.addEventListener('storage', onStorage);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', keepAlive);
    window.removeEventListener('online', keepAlive);
    window.removeEventListener('storage', onStorage);
  };
}
